import axios from 'axios';
import { REST_HOSTNAME } from '../Config';
import { SpeakerRole } from './SpeakerApi';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export class Sentence {
    id: number;
    text: string;
    simple_text: string;
    class_name: string;

    constructor(id: number, text: string, simple_text: string) {
        this.id = id;
        this.text = text;
        this.simple_text = simple_text;
        this.class_name = "SentenceModel";
    }
}

export class SentenceChinese extends Sentence {
    pinyin: string[];
    processing_queue?: number;

    constructor(id: number, text: string, simple_text: string, pinyin: string[]) {
        super(id, text, simple_text,);
        this.pinyin = pinyin;
        this.class_name = "SentenceChineseModel";
    }
}

export type MaxProcessingQueueResponse = {
    [key: number]: number; // Index signature: keys are strings, values are integers
};

export const SentenceApi = {
    getById: function (sentenceId: number) {
        return axiosInstance.request<Sentence>({
            method: "GET",
            url: `/sentence/${sentenceId}`
        })//.then(response => SentenceFactory(response.data));
    },
    getSortedByRecentSentenceScore: function () {
        return axiosInstance.request<Sentence[]>({
            method: "GET",
            url: `/sentence/recent_score`
        })//.then(response => response.data.map(SentenceFactory));
    },
    getSortedByMostSentenceScore: function () {
        return axiosInstance.request<Sentence[]>({
            method: "GET",
            url: `/sentence/most_score`
        })//.then(response => response.data.map(SentenceFactory));
    },
    getMaxProcessingQueue: function () {
        return axiosInstance.request<{ [key: number]: number }>({
            method: "GET",
            url: `/max_processing_queue`,
        })
            .then((response) => {
                if (response.status < 300) {
                    const data = response.data;
                    const value = data[0]; // Access the value for the key '0'
                    return value; // Return the extracted value
                }
                throw new Error("Error fetching max processing queue: " + response.status);
            });
    },
    getMaxProcessingQueueForDialogPrompt: function (dialogId: number) {
        return axiosInstance.request<MaxProcessingQueueResponse>({
            method: "GET",
            url: `/max_processing_queue/dialog/${dialogId}`
        })
    },
    getMaxProcessingQueueForDialog: function (lessonId: number) {
        return axiosInstance.request<MaxProcessingQueueResponse>({
            method: "GET",
            url: `/max_processing_queue/lesson/${lessonId}`
        })
    },
    getMaxProcessingQueueForLesson: function (courseId: number) {
        return axiosInstance.request<MaxProcessingQueueResponse>({
            method: "GET",
            url: `/max_processing_queue/course/${courseId}`
        })
    },
    searchSentenceByText: function (text_query: string) {
        return axiosInstance.request<Sentence[]>({
            method: "GET",
            url: `/sentence/search?text_query=${text_query}`
        })//.then(response => response.data.map(SentenceFactory));
    },
    regenerateSentenceAudio: function (sentenceId: number, speakerRoles?: SpeakerRole[]) {
        return axiosInstance.request({
            method: "PUT",
            url: `/sentence/${sentenceId}/regenerate_flag`,
            data: speakerRoles ? { speaker_roles: speakerRoles } : undefined,
        })
    },
    getSentenceProcessingQueue: function () {
        return axiosInstance.request<Sentence[]>({
            method: "GET",
            url: `/sentence_processing_queue`
        })//.then(response => response.data.map(SentenceFactory));
    },
    getSentenceChinesePinyin: function (sentenceId: number) {
        return axiosInstance.request<Sentence>({
            method: "GET",
            url: `/sentence/${sentenceId}`
        })
            .then(response => {
                const sentence = SentenceFactory(response.data)
                if (sentence instanceof SentenceChinese) {
                    return sentence.pinyin
                }
                console.error(`Error getting pinyin, not chinese sentence for sentence id=${sentenceId} text=${sentence.text}`)
                return ["ERROR getting pinyin"]
            });
    },
    getTextPinyin: function (text: string, signal?: AbortSignal) {
        return axiosInstance.request<string[][]>({
            method: "PUT",
            url: `/sentence_pinyin`,
            signal,
            ...(signal ? { timeout: 10000 } : {}),
            data: { 'text': text }
        })
    },
    isTextPinyinValid: function (text: string, pinyin: string[], isAnswerTagValid: boolean = false) {
        return axiosInstance.request<string[]>({
            method: "PUT",
            url: `/sentence_valid_pinyin`,
            data: { 'text': text, 'pinyin': pinyin, 'is_answer_tag_valid': isAnswerTagValid }
        })
    }
}

export function SentenceFactory(obj: any): Sentence | SentenceChinese {
    if (obj.class_name === "SentenceChineseModel") {
        return new SentenceChinese(obj.id, obj.text, obj.simple_text, obj.pinyin);
    } else if (obj.class_name === "SentenceModel") {
        return new Sentence(obj.id, obj.text, obj.simple_text);
    }
    throw new Error(`Unknown class_name ${obj.class_name}`)
}
